import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { operatorLanguages } from '../services/localization/brandLanguages';
import { brandFromServiceProvider } from '../services/branding/brandFromServiceProvider';
import AuthorizationTokenStorage from '../services/auth/AuthorizationTokenStorage';
import WebViewCommunicatorResource from '../services/resources/WebViewCommunicatorResource';
import ReduxHooks from '../store/ReduxHooks';
import ProfileManager from '../services/api/ProfileManager';
import { actionCreators } from '../store/actions';
import BackendApiClient from '../services/api/BackendApiClient';
import IntercomWidget from '../widgets/IntercomWidget';
import { syncPartnerSlugForLogout } from '../services/branding/partnerSlugStorage';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(AuthorizationTokenStorage.getToken());

  const isLoggedIn = !!token;

  useEffect(() => {
    if (!token) {
      return;
    }

    loadProfile();
    WebViewCommunicatorResource.forwardAuthToken({
      userToken: token,
    });
  }, [token]);

  const context = useMemo(
    () => ({
      isLoggedIn,
      logIn: async (data) => {
        try {
          await AuthorizationTokenStorage.saveToken(data.token);
          BackendApiClient.setAuthToken(data.token);
          setToken(data.token);
        } catch (error) {
          console.error('Failed to log the user in', error);
        }
      },
      logOut: () => {
        try {
          // Before the store is wiped: the login page we land on keeps this session's brand.
          syncPartnerSlugForLogout(ReduxHooks.getStore().company?.company?.service_provider);
          AuthorizationTokenStorage.deleteToken();
          ReduxHooks.clearReduxStore();
          setToken(null);
        } catch (error) {
          console.error('Failed to log the user out', error);
        }
      },
    }),
    [isLoggedIn],
  );

  async function loadProfile() {
    const { data, success } = await ProfileManager.get();

    if (!success) {
      return;
    }

    ReduxHooks.dispatch(actionCreators.company.set(data.data));
    applyPreferredLanguage(data.data);
  }

  /**
   * After login the staff account's own preference wins, and without one the outlet's
   * configured language applies — so staff at a French restaurant get a French orders
   * portal while an English-speaking operator keeps English. Before login the brand
   * decides (see BrandProvider / brandLanguages).
   *
   * `company.lang` is staff_accounts.lang and is NULL until the operator actually taps
   * the switcher, which is what makes the fall-through to `company.language` reachable.
   * It used to be NOT NULL with a 'de' default and written at account creation, so this
   * `??` never fell through and the outlet language reached the storefront and nothing
   * else.
   *
   * Filtered through the partner's own language set — brandFromServiceProvider, the same
   * derivation BrandProvider uses — rather than the outlet's region: `company.locales`
   * is geography with no partner input in it, so it never expressed "what this partner
   * offers". The outlet's own language is always admitted on top (see
   * operatorLanguages), or configuring an outlet in Polish would leave its terminal in
   * German.
   *
   * Applied through i18next directly: the value came from the server, so persisting it
   * back to the account would be a pointless round trip.
   */
  function applyPreferredLanguage(company) {
    const preferred = company?.lang ?? company?.language;
    const offered = operatorLanguages(
      brandFromServiceProvider(company?.service_provider, company?.locales),
      company?.language,
    );

    if (preferred && offered.includes(preferred) && i18n.language?.split(/[-_]/)[0] !== preferred) {
      i18n.changeLanguage(preferred);
    }
  }

  return (
    <AuthContext.Provider value={context}>
      {children}
      <IntercomWidget />
    </AuthContext.Provider>
  );
}
